import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Cap, Level } from "../../../utils/rbacCatalog";
import { useForm, useWatch } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { useTranslation } from "react-i18next";
import { Label } from "flowbite-react";
import { HiOutlinePhotograph } from "react-icons/hi";
import { toast } from "react-toastify";
import parse from "html-react-parser";

import { CustomInput } from "../../atoms/CustomInput";
import { CustomButton } from "../../atoms/CustomButton";
import { ModalShell } from "../../atoms/ModalShell";
import { CharacterCounter } from "../../atoms/CharacterCounter";
import { Switch } from "../../atoms/Switch";
import EventImageUpload from "../../atoms/EventImageUpload";
import {
  UIEventForm,
  UITicket,
  localDatetimeToUtc,
  utcToLocalDatetime,
} from "../../../utils/eventHelpers";
import useAuth from "../../../hooks/useAuth";
import { useGetGroups } from "../../../hooks/groups";
import { PlanTier, usePermissions } from "../../../utils/permissions";

import { TicketListItem } from "./TicketListItem";
import { TicketForm } from "./TicketForm";
import { createEventSchema } from "./schemas";
import {
  DEFAULT_EVENT_VALUES,
  DEFAULT_TICKET_VALUES,
  EVENT_NAME_MAX_LENGTH,
  EVENT_DESCRIPTION_MAX_LENGTH,
  EVENT_LOCATION_MAX_LENGTH,
  T_PREFIX,
} from "./constants";
import { EventModalProps, EventFormData, TicketFormData } from "./types";

export const EventModal: React.FC<EventModalProps> = ({
  show,
  onClose,
  onSubmit,
  initialData,
  isEditMode = false,
  isSubmitting = false,
  onShowPaidTicketUpgrade,
}) => {
  const { t } = useTranslation();

  const { customer_identification, user } = useAuth();
  const permissions = usePermissions(user);
  const { data: groupsData, isLoading: isLoadingGroups } = useGetGroups({
    customer_identification,
    entityIdentification: user.entity_identification,
    limit: 50,
    sortOrder: "DESC",
    pageParam: 0,
    enabled: show && permissions.can(Cap.GROUP_MANAGEMENT, Level.VIEW),
  });

  const [ticketTypes, setTicketTypes] = useState<UITicket[]>([]);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [editingTicketId, setEditingTicketId] = useState<string | null>(null);
  const [eventImageUrl, setEventImageUrl] = useState<string | undefined>();

  const eventSchema = useMemo(() => createEventSchema(t), [t]);

  const {
    control: eventControl,
    handleSubmit: handleEventSubmit,
    formState: { errors: eventErrors },
    reset: resetEventForm,
    setValue: setEventValue,
  } = useForm<EventFormData>({
    resolver: joiResolver(eventSchema),
    defaultValues: DEFAULT_EVENT_VALUES,
  });

  const eventActive = useWatch({ control: eventControl, name: "active" });
  const eventName = useWatch({ control: eventControl, name: "event_name" });
  const eventDescription = useWatch({
    control: eventControl,
    name: "event_description",
  });
  const eventLocation = useWatch({
    control: eventControl,
    name: "location",
  });

  const editingTicketDefaults = useMemo(() => {
    if (!editingTicketId) return DEFAULT_TICKET_VALUES;

    const ticket = ticketTypes.find((t) => t.ticket_id === editingTicketId);
    if (!ticket) return DEFAULT_TICKET_VALUES;

    return {
      name: ticket.name,
      description: ticket.description || "",
      price: ticket.price,
      currency: ticket.currency,
      quantity_total: ticket.quantity_total,
      min_per_order: ticket.min_per_order || 1,
      max_per_order: ticket.max_per_order || 2,
      show_remaining_tickets: ticket.show_remaining_tickets !== false,
      sale_start_date: utcToLocalDatetime(ticket.sale_start_date),
      sale_end_date: utcToLocalDatetime(ticket.sale_end_date),
      group_identification: ticket.group_identification || "",
    };
  }, [editingTicketId, ticketTypes]);

  useEffect(() => {
    if (!show) return;

    if (initialData) {
      resetEventForm({
        event_name: initialData.event_name || "",
        event_description: initialData.event_description || "",
        location: initialData.location || "",
        active: initialData.active !== false,
        event_image_url: initialData.event_image_url || "",
      });
      setTicketTypes(initialData.ticket_types || []);
      setEventImageUrl(initialData.event_image_url);
    } else {
      resetEventForm(DEFAULT_EVENT_VALUES);
      setTicketTypes([]);
      setEventImageUrl(undefined);
    }

    setShowTicketForm(false);
    setEditingTicketId(null);
  }, [show, initialData, resetEventForm]);

  const handleAddNewTicket = useCallback(() => {
    setEditingTicketId(null);
    setShowTicketForm(true);
  }, []);

  const handleEditTicket = useCallback((ticketId: string) => {
    setEditingTicketId(ticketId);
    setShowTicketForm(true);
  }, []);

  const handleRemoveTicket = useCallback((ticketId: string) => {
    setTicketTypes((prev) => prev.filter((t) => t.ticket_id !== ticketId));
  }, []);

  const handleCancelTicketForm = useCallback(() => {
    setEditingTicketId(null);
    setShowTicketForm(false);
  }, []);

  const handleTicketSubmit = useCallback(
    (data: TicketFormData) => {
      let quantitySold = 0;
      if (editingTicketId) {
        const existingTicket = ticketTypes.find(
          (t) => t.ticket_id === editingTicketId
        );
        quantitySold = existingTicket?.quantity_sold || 0;
      }

      const newTicket: UITicket = {
        ticket_id:
          editingTicketId ||
          `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: data.name,
        description: data.description,
        price: Number(data.price),
        currency: data.currency || "USD",
        quantity_total: data.quantity_total
          ? Number(data.quantity_total)
          : undefined,
        quantity_sold: quantitySold,
        min_per_order: data.min_per_order,
        max_per_order: data.max_per_order,
        show_remaining_tickets: data.show_remaining_tickets,
        sale_start_date: data.sale_start_date
          ? localDatetimeToUtc(data.sale_start_date)
          : undefined,
        sale_end_date: data.sale_end_date
          ? localDatetimeToUtc(data.sale_end_date)
          : undefined,
        group_identification: data.group_identification || undefined,
      };

      if (editingTicketId) {
        setTicketTypes((prev) =>
          prev.map((t) => (t.ticket_id === editingTicketId ? newTicket : t))
        );
      } else {
        setTicketTypes((prev) => [...prev, newTicket]);
      }

      setEditingTicketId(null);
      setShowTicketForm(false);
    },
    [editingTicketId, ticketTypes]
  );

  const handleEventFormSubmit = useCallback(
    async (data: EventFormData) => {
      if (showTicketForm) {
        toast.error(t(`${T_PREFIX}.validationErrors.pendingTicketForm`));
        return;
      }

      if (ticketTypes.length === 0) {
        toast.error(t(`${T_PREFIX}.validationErrors.ticketTypesRequired`));
        return;
      }

      const eventForm: UIEventForm = {
        event_name: data.event_name,
        event_description: data.event_description,
        location: data.location,
        event_image_url: eventImageUrl,
        ticket_types: ticketTypes,
        active: data.active,
      };

      await onSubmit(eventForm);
    },
    [showTicketForm, ticketTypes, eventImageUrl, onSubmit, t]
  );

  const handleImageUploaded = useCallback((cdnPath: string) => {
    setEventImageUrl(cdnPath);
  }, []);

  const handleImageRemoved = useCallback(() => {
    setEventImageUrl(undefined);
  }, []);

  const handleActiveToggle = useCallback(
    (checked: boolean) => {
      setEventValue("active", checked);
    },
    [setEventValue]
  );

  return (
    <ModalShell
      show={show}
      onClose={onClose}
      size="3xl"
      title={
        isEditMode
          ? t(`${T_PREFIX}.createModal.editTitle`)
          : t(`${T_PREFIX}.createModal.newTitle`)
      }
      footerClassName="justify-between"
      footer={
        <>
          <p className="text-xs text-gray-500 italic">
            {t(`${T_PREFIX}.createModal.note`)}
          </p>
          <div className="ml-auto flex justify-end gap-3">
            <CustomButton
              title={t(`${T_PREFIX}.actions.cancel`) as string}
              onClick={onClose}
              variant="secondary"
              disabled={isSubmitting}
            />
            {permissions.can(Cap.EVENTS, Level.WRITE) && (
              <CustomButton
                title={
                  isSubmitting
                    ? (t(`${T_PREFIX}.actions.saving`) as string)
                    : (t(`${T_PREFIX}.actions.saveEvent`) as string)
                }
                onClick={handleEventSubmit(handleEventFormSubmit)}
                variant="primary"
                disabled={isSubmitting}
              />
            )}
          </div>
        </>
      }
    >
      <form
        onSubmit={handleEventSubmit(handleEventFormSubmit)}
        aria-label="Event form"
      >
        <div className="space-y-4">
          <div>
            <CustomInput
              name="event_name"
              control={eventControl}
              error={eventErrors.event_name}
              helperText={eventErrors.event_name?.message}
              label={t(`${T_PREFIX}.eventForm.nameLabel`) as string}
              placeholder={t(`${T_PREFIX}.eventForm.namePlaceholder`) as string}
              maxLength={EVENT_NAME_MAX_LENGTH}
            />
            <CharacterCounter
              currentLength={eventName?.length || 0}
              maxLength={EVENT_NAME_MAX_LENGTH}
            />
          </div>

          <div>
            <CustomInput
              name="event_description"
              control={eventControl}
              error={eventErrors.event_description}
              helperText={eventErrors.event_description?.message}
              label={t(`${T_PREFIX}.eventForm.descriptionLabel`) as string}
              placeholder={
                t(`${T_PREFIX}.eventForm.descriptionPlaceholder`) as string
              }
              rows={3}
              maxLength={EVENT_DESCRIPTION_MAX_LENGTH}
            />
            <CharacterCounter
              currentLength={eventDescription?.length || 0}
              maxLength={EVENT_DESCRIPTION_MAX_LENGTH}
            />
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div>
              <Label
                htmlFor="event_active"
                value={t(`${T_PREFIX}.eventForm.statusLabel`) as string}
                className="font-semibold"
              />
              <p className="text-xs text-gray-600 mt-1">
                {eventActive
                  ? t(`${T_PREFIX}.eventForm.statusActive`)
                  : t(`${T_PREFIX}.eventForm.statusInactive`)}
              </p>
            </div>
            <div className="flex items-center gap-3 mt-3 justify-end">
              <span
                className={`text-sm font-semibold ${
                  eventActive ? "text-green-700" : "text-red-600"
                }`}
              >
                {eventActive
                  ? t(`${T_PREFIX}.eventCard.active`)
                  : t(`${T_PREFIX}.eventCard.inactive`)}
              </span>
              <Switch
                id="event_active"
                checked={eventActive}
                onChange={handleActiveToggle}
              />
            </div>
          </div>

          <div>
            <Label
              htmlFor="event_image"
              value={t(`${T_PREFIX}.eventForm.imageLabel`) as string}
              className="text-gray-500 text-xs font-normal leading-none"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <EventImageUpload
                imageUrl={eventImageUrl}
                onImageUploaded={handleImageUploaded}
                onImageRemoved={handleImageRemoved}
              />

              {permissions.isAtLeastTier(PlanTier.PREMIUM) && (
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                  <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <HiOutlinePhotograph
                      className="text-blue-600"
                      size={18}
                      aria-hidden="true"
                    />
                    {t(`${T_PREFIX}.eventForm.imageTips.title`)}
                  </h5>
                  <ul className="space-y-2 text-xs text-gray-700">
                    {["aspectRatio", "fileSize", "format", "content"].map(
                      (tipKey) => (
                        <li key={tipKey} className="flex items-start gap-2">
                          <span
                            className="text-blue-600 mt-0.5"
                            aria-hidden="true"
                          >
                            •
                          </span>
                          <span>
                            {parse(
                              t(
                                `${T_PREFIX}.eventForm.imageTips.${tipKey}`
                              ) as string
                            )}
                          </span>
                        </li>
                      )
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div>
            <CustomInput
              name="location"
              control={eventControl}
              error={eventErrors.location}
              helperText={eventErrors.location?.message}
              label={t(`${T_PREFIX}.eventForm.locationLabel`) as string}
              placeholder={
                t(`${T_PREFIX}.eventForm.locationPlaceholder`) as string
              }
              rows={4}
              maxLength={EVENT_LOCATION_MAX_LENGTH}
            />
            <CharacterCounter
              currentLength={eventLocation?.length || 0}
              maxLength={EVENT_LOCATION_MAX_LENGTH}
            />
          </div>

          <div className="border-t border-gray-200 pt-4">
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
              <h4 className="text-base font-semibold text-gray-900">
                {t(`${T_PREFIX}.ticketSection.title`)}
              </h4>
              {ticketTypes.length > 0 && !showTicketForm && (
                <span className="text-sm text-gray-500">
                  {t(`${T_PREFIX}.ticketSection.configured`, {
                    count: ticketTypes.length,
                  })}
                </span>
              )}
            </div>

            {ticketTypes.length > 0 && (
              <div className="mb-4 space-y-2" role="list">
                {ticketTypes.map((ticket, index) => (
                  <TicketListItem
                    key={ticket.ticket_id || `temp-ticket-${index}`}
                    ticket={ticket}
                    index={index}
                    onEdit={
                      permissions.can(Cap.EVENTS, Level.WRITE)
                        ? handleEditTicket
                        : undefined
                    }
                    onRemove={
                      permissions.can(Cap.EVENTS, Level.WRITE)
                        ? handleRemoveTicket
                        : undefined
                    }
                  />
                ))}
              </div>
            )}

            {showTicketForm && (
              <TicketForm
                key={editingTicketId || "new"}
                isEditing={!!editingTicketId}
                onSubmit={handleTicketSubmit}
                onCancel={handleCancelTicketForm}
                defaultValues={editingTicketDefaults}
                onShowPaidTicketUpgrade={onShowPaidTicketUpgrade}
                groups={groupsData?.groups}
                isLoadingGroups={isLoadingGroups}
              />
            )}

            {!showTicketForm && permissions.can(Cap.EVENTS, Level.WRITE) && (
              <CustomButton
                type="button"
                title={t(`${T_PREFIX}.ticketSection.addTicketType`) as string}
                onClick={handleAddNewTicket}
                variant="primary"
                icon="plus"
                className="w-full"
              />
            )}
          </div>
        </div>
      </form>
    </ModalShell>
  );
};

export default EventModal;
